import React from 'react'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { TEMPLATE_CATEGORY_LABELS } from '../../libs/metaTemplates'
import messagesLib from '../../libs/messages'
import { corLegivel } from '../../libs/corLegivel'

// O ⓘ ao lado do horário: quanto este envio custou e por quê. O número vem
// pronto do master (message.custo), calculado com a mesma regra do débito —
// aqui só se explica. Sem `custo` (instância que não é da API Oficial, ou
// master antigo) o ícone simplesmente não aparece.

const preco = (v) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`

const data = (d) => new Date(d).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })

function nomeDaCategoria(custo) {
  const cat = String(custo.categoria || '').toUpperCase()
  const rotulo = TEMPLATE_CATEGORY_LABELS[cat] || (cat ? cat.toLowerCase() : null)
  if (!rotulo) return 'Sem categoria'
  return custo.categoriaInferida ? `${rotulo} (estimada)` : rotulo
}

// Título, valor em destaque e a frase do porquê, por situação.
export function explicarCusto(message) {
  const c = message.custo
  const taxa = c.regime === 'taxaServico'
  const meta = c.custoMeta

  // Texto livre da API Oficial (envio de teste): o master manda custo zerado
  // em qualquer situação — não entra no saldo nem tem custo Meta estimado.
  if (message.textoLivre === true) {
    return {
      titulo: 'Custo final deste envio',
      valor: 0,
      porque: 'Texto livre de teste: não é cobrado, tenha saído ou não.',
      zero: true,
    }
  }

  if (c.situacao === 'cobrada') {
    return {
      titulo: taxa ? 'Taxa de serviço deste envio' : 'Custo final deste envio',
      valor: c.valor,
      porque: taxa
        ? 'Enviada — cobrada a taxa de serviço pela categoria do modelo. A Meta cobra a parte dela direto no seu cartão.'
        : c.categoriaInferida
          ? 'Enviada — como a categoria não foi confirmada no envio, vale o preço padrão do seu pacote.'
          : 'Enviada — cobrada pelo preço da categoria do modelo no seu pacote.',
      zero: false,
    }
  }
  if (c.situacao === 'prevista') {
    return {
      titulo: 'Ainda não saiu',
      valor: 0,
      porque: c.previsto !== null && c.previsto !== undefined
        ? `Está na fila ou retida. Será cobrada ${preco(c.previsto)} quando for enviada.`
        : 'Está na fila ou retida. Nada é cobrado antes de sair.',
      zero: true,
    }
  }
  if (c.situacao === 'semCobranca') {
    return {
      titulo: 'Custo estimado na Meta',
      valor: meta ? meta.valor : 0,
      porque: 'Pago direto no seu cartão cadastrado na Meta. Estimativa pela tabela oficial de preços.',
      zero: !meta,
    }
  }
  if (c.situacao === 'antesDaCobranca') {
    return { titulo: 'Custo final deste envio', valor: 0, porque: 'Enviada antes do início da cobrança por mensagem.', zero: true }
  }
  return {
    titulo: 'Custo final deste envio',
    valor: 0,
    porque: message.status === 'erro'
      ? 'Não cobrada. A mensagem não saiu, então nada foi debitado do seu saldo.'
      : 'Não cobrada: esta mensagem não foi enviada.',
    zero: true,
  }
}

function Conteudo({ message }) {
  const c = message.custo
  const e = explicarCusto(message)
  const modelo = message.templateName || messagesLib.getWhatsappCloudApiTemplateData(message.message || '')?.templateName
  const linhas = [
    ['Categoria', message.textoLivre === true ? 'Texto livre' : nomeDaCategoria(c)],
    modelo ? ['Modelo', modelo] : null,
    c.faixaDesde && c.situacao !== 'naoCobrada' ? ['Tabela vigente', `desde ${data(c.faixaDesde)}`] : null,
    c.situacao === 'prevista' && c.previsto ? ['Previsto', preco(c.previsto)] : null,
    c.custoMeta && c.situacao !== 'semCobranca'
      ? [c.custoMeta.previsto ? 'Meta (previsto)' : 'Custo na Meta*', preco(c.custoMeta.valor)]
      : null,
  ].filter(Boolean)

  return (
    <div style={{ width: 236, padding: '4px 2px' }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', opacity: 0.6 }}>{e.titulo}</div>
      <div style={{ fontSize: 21, fontWeight: 900, margin: '2px 0 8px', fontVariantNumeric: 'tabular-nums', color: e.zero ? '#7ee2b8' : 'inherit' }}>
        {preco(e.valor)}
      </div>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 11.5 }}>
        {linhas.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ opacity: 0.6 }}>{k}</dt>
            <dd style={{ margin: 0, textAlign: 'right', fontWeight: 650, overflowWrap: 'anywhere' }}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p style={{ margin: '8px 0 0', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.12)', fontSize: 11.5, lineHeight: 1.45, opacity: 0.8 }}>
        {e.porque}
        {c.custoMeta && c.situacao !== 'semCobranca' && ' * Estimado pela tabela oficial da Meta.'}
      </p>
    </div>
  )
}

export default function CustoDoEnvio({ message }) {
  const theme = useTheme()
  if (!message || !message.custo) return null

  return (
    <Tooltip
      title={<Conteudo message={message} />}
      arrow
      placement="top-end"
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      componentsProps={{
        tooltip: { sx: { bgcolor: '#101318', color: '#eef2f5', borderRadius: '11px', p: 1.25, boxShadow: '0 14px 34px -8px rgba(0,0,0,.55)', maxWidth: 'none' } },
        arrow: { sx: { color: '#101318' } },
      }}
    >
      <button
        type="button"
        aria-label="Ver o custo deste envio"
        className="inline-grid place-items-center rounded-full transition-colors hover:opacity-100"
        style={{
          width: 20,
          height: 20,
          flex: 'none',
          color: corLegivel(theme.palette.primary.main, theme.palette.background.paper, 3),
          background: theme.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
          opacity: 0.85,
        }}
      >
        <InfoOutlinedIcon style={{ fontSize: 14 }} />
      </button>
    </Tooltip>
  )
}
